import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://reconecta.com.br';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${siteUrl}/#organization`,
  name: 'RE_CONECTA Tecnologia',
  alternateName: 'RECONECTA Tecnologia',
  description:
    'Referência em segurança eletrônica, CFTV, controle de acesso e sistemas de alarme no Sul do Brasil. Implantação e manutenção de sistemas críticos com rigor técnico para empresas de renome nacional.',
  url: siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: 'https://static.readdy.ai/image/c4cc08b99909642a7f7632376a1bb059/bdbf4a9d48d2468543b4df36d235abc8.png',
    width: 200,
    height: 200,
  },
  image: 'https://static.readdy.ai/image/c4cc08b99909642a7f7632376a1bb059/bdbf4a9d48d2468543b4df36d235abc8.png',
  telephone: '+55-54-997056430',
  email: 're-conecta@re-conecta.com',
  priceRange: '$$',
  areaServed: [
    {
      '@type': 'State',
      name: 'Rio Grande do Sul',
      addressCountry: 'BR',
    },
    {
      '@type': 'State',
      name: 'Santa Catarina',
      addressCountry: 'BR',
    },
    {
      '@type': 'State',
      name: 'Paraná',
      addressCountry: 'BR',
    },
  ],
  address: {
    '@type': 'PostalAddress',
    addressRegion: 'RS',
    addressCountry: 'BR',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: -29.1678,
    longitude: -51.1794,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
      description: 'Suporte técnico emergencial 24/7',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Serviços de Segurança Eletrônica',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CFTV & Monitoramento',
          description:
            'Instalação e manutenção de circuitos fechados de TV com câmeras IP de alta definição, gravadores digitais e monitoramento remoto 24h.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Controle de Acesso',
          description:
            'Sistemas inteligentes de controle de entrada e saída com biometria, cartão de proximidade e integração com RH.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Sistemas de Alarme',
          description:
            'Central de alarme com sensores de presença, vibração e fumaça integrados a uma central de monitoramento profissional.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Redes & Infraestrutura',
          description:
            'Implantação de redes cabeadas e wireless de alta performance para suporte dos sistemas de segurança e operação corporativa.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Manutenção Preventiva',
          description:
            'Programa estruturado de manutenção preventiva e corretiva para garantir o pleno funcionamento dos sistemas críticos.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Integração de Sistemas',
          description:
            'Integração de hardware e software de diferentes fabricantes em uma plataforma unificada de gerenciamento e controle.',
        },
      },
    ],
  },
  sameAs: [],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/#webpage`,
  url: siteUrl,
  name: 'RE_CONECTA Tecnologia | Segurança Eletrônica, CFTV e Controle de Acesso no Sul do Brasil',
  description:
    'Referência em segurança eletrônica e sistemas críticos no Sul do Brasil. CFTV, controle de acesso, alarmes e manutenção preventiva com rigor técnico.',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    url: siteUrl,
    name: 'RE_CONECTA Tecnologia',
    inLanguage: 'pt-BR',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Início',
        item: siteUrl,
      },
    ],
  },
  dateModified: '2026-03-26',
  inLanguage: 'pt-BR',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Quais serviços de segurança eletrônica a RE_CONECTA oferece?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A RE_CONECTA Tecnologia oferece CFTV e monitoramento, controle de acesso com biometria, sistemas de alarme, redes e infraestrutura corporativa, manutenção preventiva e integração de sistemas de segurança.',
      },
    },
    {
      '@type': 'Question',
      name: 'A RE_CONECTA Tecnologia atua em qual região do Brasil?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Atuamos no Sul do Brasil, com foco no Rio Grande do Sul, Santa Catarina e Paraná, atendendo empresas de renome nacional com rigor técnico e agilidade.',
      },
    },
    {
      '@type': 'Question',
      name: 'A RE_CONECTA oferece suporte técnico 24 horas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sim. Oferecemos suporte técnico 24/7 para todos os sistemas instalados, garantindo a continuidade operacional e a proteção dos nossos clientes a qualquer hora.',
      },
    },
    {
      '@type': 'Question',
      name: 'Como posso solicitar um orçamento de segurança eletrônica?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Você pode solicitar um orçamento pelo e-mail re-conecta@re-conecta.com, pelo WhatsApp 54-997056430 ou preenchendo o formulário de contato em nosso site.',
      },
    },
  ],
};

function injectJsonLd(id: string, data: object): void {
  const existing = document.getElementById(id);
  if (existing) {
    existing.textContent = JSON.stringify(data);
    return;
  }
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

export default function SEOHead() {
  useEffect(() => {
    injectJsonLd('schema-local-business', localBusinessSchema);
    injectJsonLd('schema-web-page', webPageSchema);
    injectJsonLd('schema-faq', faqSchema);

    return () => {
      ['schema-local-business', 'schema-web-page', 'schema-faq'].forEach((id) => {
        document.getElementById(id)?.remove();
      });
    };
  }, []);

  return null;
}
